import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AppIcon, ContentNugget, ICON_SIZES, getNuggetImageUrl } from '@backrs_ui/common-ui';

import {
  getCareerPathCategoryName,
  getCareerPathIcon,
} from '@backrs_ui/grow-ui/src/pages/CareerPath/util';
import {
  StickerCardId,
  useToolCardById,
} from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';

import { Box, Button, SxProps, Theme, Typography } from '@mui/material';

type TaskCardNuggetContentProps = Readonly<{
  nugget: ContentNugget;
  onClick: () => void;
}>;

export const TaskCardNuggetContent = ({ nugget, onClick }: TaskCardNuggetContentProps) => {
  const navigate = useNavigate();

  const [didImageLoadFail, setDidImageLoadFail] = useState(false);

  const { getStickerCard } = useToolCardById({ navigate });

  const getStickerCardUrl = (id: string) => {
    const stickerCard = getStickerCard(id as StickerCardId);

    return stickerCard?.stickerUrl;
  };

  const careerCategories = useAppSelector((state) => state.careerPath.careerCategories);
  const imageUrl = getNuggetImageUrl({
    nugget,
    careerCategories,
    getCareerPathIcon,
    getStickerCardUrl,
  });

  const careerPathCategoryName =
    nugget.variant === 'career-path' && careerCategories.length > 0 && nugget.career_path_info
      ? getCareerPathCategoryName(careerCategories, nugget.career_path_info)
      : undefined;

  const shouldBorderBeAroundImage = !['competency', 'tool'].includes(nugget.variant);

  const didImageFailToLoad = imageUrl?.type === 'URL' && (!imageUrl.url || didImageLoadFail);
  const shouldShowViewButton = didImageFailToLoad || imageUrl === undefined;

  return (
    <Box
      sx={nuggetContainerSx}
      key={nugget.id}
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
    >
      <Box sx={customNuggetContainerSx}>
        {imageUrl?.type === 'URL' && imageUrl.url && (
          <Box
            component="img"
            src={imageUrl.url}
            alt={nugget.title}
            sx={nuggetImageSx(shouldBorderBeAroundImage)}
            onError={(e) => {
              setDidImageLoadFail(true);
              // Hide image if it fails to load
              (e.target as HTMLElement).style.display = 'none';
            }}
          />
        )}

        {shouldShowViewButton && (
          <Button
            id="task-card-view-button"
            variant="outlined"
            size="sm"
            hierarchy="Secondary gray"
            sx={viewButtonSx}
          >
            <AppIcon iconName="Share06" diameter="24px" color="Colors/Text/text-brand-tertiary" />
          </Button>
        )}

        {imageUrl?.type === 'COMPONENT' && imageUrl.component}

        <Box sx={nuggetTitleContainerSx}>
          <Typography variant="text-md-medium">{nugget.title}</Typography>

          {careerPathCategoryName && (
            <Typography variant="text-sm-medium">{careerPathCategoryName}</Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const nuggetContainerSx: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-2xl'],
  paddingX: backrs.spacing['spacing-3xl'],
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  cursor: 'pointer',
});

const customNuggetContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
  paddingRight: backrs.spacing['spacing-sm'],
  paddingY: backrs.spacing['spacing-sm'],
  borderRadius: backrs.radius['radius-md'],
  justifyContent: 'space-between',
});

const nuggetImageSx =
  (shouldBorderBeAroundImage: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: ICON_SIZES['64px'],
    height: ICON_SIZES['64px'],
    ...(shouldBorderBeAroundImage && {
      border: `1px solid ${backrs.colors['Colors/Base/Black']}14`, // 8% opacity
    }),
    borderRadius: backrs.radius['radius-sm'],
    objectFit: 'cover',
    flexShrink: 0,
  });

const nuggetTitleContainerSx: SxProps<Theme> = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  alignSelf: 'flex-start',
  width: '100%',
};

const viewButtonSx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Brand/300']}`,

  '&:active': {
    border: `1px solid ${backrs.colors['Brand/300']}`,
  },

  padding: backrs.spacing['spacing-xl'],

  width: ICON_SIZES['64px'],
  height: ICON_SIZES['64px'],
  minWidth: ICON_SIZES['64px'],
  minHeight: ICON_SIZES['64px'],
  maxWidth: ICON_SIZES['64px'],
  maxHeight: ICON_SIZES['64px'],
});
